import type { ISectionedFilters } from "types/filters";
import type { TPaginationSortFilterOptions, TPaginationSortOptions } from "types/pagination";
import type { Require, TJsonSchema } from "types/utilTypes";
import type { TTicketDuration } from "utils/durationsOptions";

import { type List, Map } from "immutable";
import omitBy from "lodash/omitBy";
import { IntegrationModel } from "models/IntegrationModel";
import { apiReq } from "utils/api/apiReq";
import { addSearchParam, fromPaginationSortFilterOptions, paginationSortFilterToBody } from "utils/api/urlSearchParams";
import { type IPaginationResponse, withPagination } from "utils/pagination";

export type TGetIntegrationsOptions = (TPaginationSortFilterOptions & { search?: string; withDeleted?: boolean }) | undefined;
export type TSearchIntegrationsOptions = (TPaginationSortOptions & {
  filters?: ISectionedFilters;
  onlyFgaAuthorized?: boolean;
  search?: string;
  withDeleted?: boolean;
}) | undefined;

export async function getIntegration(integrationId: string): Promise<IntegrationModel> {
  const { data } = await apiReq("GET", `/v1/integrations/${integrationId}`);

  return IntegrationModel.fromServerData(data);
}
export async function legacyGetIntegrations(
  options?: Omit<TPaginationSortFilterOptions, "page" | "perPage"> & { search?: string; withDeleted?: boolean },
): Promise<Map<string, IntegrationModel>>;
export async function legacyGetIntegrations(
  options: (Require<TPaginationSortFilterOptions, "page"> | Require<TPaginationSortFilterOptions, "perPage">) & {
    search?: string;
    withDeleted?: boolean;
  },
): Promise<IPaginationResponse<IntegrationModel>>;
export async function legacyGetIntegrations(
  options?: TGetIntegrationsOptions,
): Promise<IPaginationResponse<IntegrationModel> | Map<string, IntegrationModel>> {
  const searchParams = fromPaginationSortFilterOptions(options);

  if (options?.withDeleted === true) {
    searchParams.set("withDeleted", "true");
  }

  if (options?.search) {
    addSearchParam(searchParams, options.search);
  }
  const querystring = searchParams.size ? `?${searchParams.toString()}` : "";

  if (options?.page !== undefined || options?.perPage !== undefined) {
    return withPagination(
      (integration: Record<string, unknown>) => IntegrationModel.fromServerData(integration),
      () => apiReq("GET", `/v1/integrations${querystring}`),
    );
  }

  const { data } = await apiReq("GET", `/v1/integrations${querystring}`);

  return Map<string, IntegrationModel>(
    data
      .map((integration: unknown) => IntegrationModel.fromServerData(integration))
      .map((integration: IntegrationModel) => [integration.id, integration]),
  );
}

export async function searchIntegrations(
  options?: Omit<TSearchIntegrationsOptions, "page" | "perPage">,
): Promise<Map<string, IntegrationModel>>;
export async function searchIntegrations(
  options: (Require<NonNullable<TSearchIntegrationsOptions>, "page"> | Require<NonNullable<TSearchIntegrationsOptions>, "perPage">),
): Promise<IPaginationResponse<IntegrationModel>>;
export async function searchIntegrations(
  options?: TSearchIntegrationsOptions,
): Promise<IPaginationResponse<IntegrationModel> | Map<string, IntegrationModel>> {
  options = options || {};
  const body: Record<string, unknown> = paginationSortFilterToBody(options, false);
  if (options.withDeleted) body.withDeleted = true;
  if (options.onlyFgaAuthorized) body.onlyFgaAuthorized = options.onlyFgaAuthorized;

  if (options.page !== undefined || options.perPage !== undefined) {
    return withPagination(
      (integration: Record<string, unknown>) => IntegrationModel.fromServerData(integration),
      () => apiReq("POST", "/v1/integrations/search", body),
    );
  }

  const { data } = await apiReq("POST", "/v1/integrations/search", body);
  return Map<string, IntegrationModel>(
    (data as unknown[])
      .map((integration) => IntegrationModel.fromServerData(integration))
      .map((integration) => [integration.id, integration]),
  );
}

export const deleteIntegration = async (integrationId: string): Promise<IntegrationModel> => {
  const { data } = await apiReq("DELETE", `/v1/integrations/${integrationId}`);

  return IntegrationModel.fromServerData(data);
};

export const updateIntegrationImage = async (integrationId: string, image: File): Promise<void> => {
  const formData = new FormData();
  formData.append("file", image);
  await apiReq("PUT", `/v1/integrations/${integrationId}/image`, formData, { multipart: true });
};

export const restoreIntegrationImageToDefault = async (integrationId: string): Promise<void> => {
  await apiReq("PUT", `/v1/integrations/${integrationId}/image/restore`);
};

export interface ICheckIntegration {
  isValidConfig: boolean;
  message: string;
}
export interface IIntegrationCreationData extends Omit<IIntegrationSharedData, "applicationId"> {
  adapterName: string;
  defaultRequestable: boolean;
}
export interface IIntegrationSharedData {
  agentTokenId: null | string;
  allowActorsSelfMapping: boolean;
  allowedDurations: List<TTicketDuration> | null;
  applicationId: string;
  autoAssignRecommendedResourceMaintainers: boolean;
  autoAssignRecommendedResourceOwner: boolean;
  canCreateActors: boolean;
  canEditPermissions: boolean;
  configuration: null | Record<string, unknown>;
  defaultApprovalAlgorithmId: string;
  name: string;
  notifyAboutExternalPermissions: boolean;
  oauthConfiguration: boolean;
  ownerUserId: string;
  readonly: boolean;
  requestable: boolean;
}

export type TIntegrationUpdateData = Partial<IIntegrationSharedData> & {
  defaultRequestable?: {
    updateAllResources: boolean;
    value: boolean;
  };
  id: string;
  maintainers?: IIntegrationMaintainer[] | null;
  resourcesSyncIntervalInMinutes?: number;
};

interface ICheckIntegrationData {
  adapterName: string;
  agentTokenId?: string;
  configuration: null | Record<string, unknown>;
  integrationId?: string;
  schemas?: TJsonSchema[];
}
interface IIntegrationMaintainer {
  id: string;
  type: "directoryGroup" | "user";
}

export async function checkIntegration(checkConfigData: ICheckIntegrationData) {
  const { data } = await apiReq("POST", "/v1/integrations/checkConfiguration", checkConfigData);
  return data as ICheckIntegration;
}

export async function createIntegration(integration: IIntegrationCreationData): Promise<IntegrationModel> {
  const { data } = await apiReq("POST", "/v1/integrations", integration);

  return IntegrationModel.fromServerData(data);
}

export async function editIntegration(integration: TIntegrationUpdateData): Promise<IntegrationModel> {
  const body = omitBy(integration, (value) => value === undefined);
  const { data } = await apiReq("PUT", `/v1/integrations/${integration.id}`, body);

  return IntegrationModel.fromServerData(data);
}

export async function syncIntegration(integrationId: string) {
  const { data } = await apiReq("POST", `/v1/integrations/${integrationId}/sync`);
  return IntegrationModel.fromServerData(data);
}
